import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@apollo/client/react';
import { Button, Card, HStack, Text, VStack } from '@chakra-ui/react';
import { useAdvisorsCollectionLoadMore } from '@src/components/sco/Advisors/hooks/useAdvisorsCollectionLoadMore';
import { OpportunitiesAccessGate, OpportunitiesLayout } from '@src/components/sco/Advisors/layout/OpportunitiesLayout';
import { useAdvisorsPaths } from '@src/components/sco/Advisors/paths';
import { AdvisorsLoadMore } from '@src/components/sco/Advisors/shared/AdvisorsLoadMore';
import { AdvisorsQueryState } from '@src/components/sco/Advisors/shared/AdvisorsQueryState';
import { formatWhen, personName } from '@src/components/sco/Advisors/utils/sessionFormat';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { ADVISOR_ACTIVE_ENGAGEMENTS } from '@src/gql/queries/AdvisorOpportunities';
import { ScoAdvisorEngagement, ScoAdvisorEngagementCollection } from '@src/graphql-types';
const PAGE_SIZE = 20;
type ActiveData = {
  advisorEngagements?: ScoAdvisorEngagementCollection | null;
};
const EngagementsBody = () => {
  const $ = _c(19);
  const paths = useAdvisorsPaths();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      variables: {
        pageSize: PAGE_SIZE,
        page: 1
      },
      fetchPolicy: "network-only"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const query = useQuery(ADVISOR_ACTIVE_ENGAGEMENTS, t0);
  let t1;
  if ($[1] !== query.data?.advisorEngagements?.items) {
    t1 = query.data?.advisorEngagements?.items ?? [];
    $[1] = query.data?.advisorEngagements?.items;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = t1.filter(_temp);
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const firstPageActive = t2;
  const t3 = query.data?.advisorEngagements?.pagination?.hasNextPage;
  let t4;
  if ($[5] !== firstPageActive || $[6] !== query.fetchMore || $[7] !== t3) {
    t4 = {
      fetchMore: query.fetchMore,
      field: "advisorEngagements",
      firstPageItems: firstPageActive,
      hasNextPage: t3
    };
    $[5] = firstPageActive;
    $[6] = query.fetchMore;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const {
    items: active,
    loadMore,
    loadingMore,
    hasNextPage
  } = useAdvisorsCollectionLoadMore(t4);
  const t5 = query.loading && !query.data;
  let t6;
  if ($[9] !== active || $[10] !== hasNextPage || $[11] !== loadMore || $[12] !== loadingMore || $[13] !== paths) {
    t6 = active.length === 0 ? <Card.Root variant="subtle"><Card.Body><VStack align="stretch" gap={3}><Text color="colorPalette.fg/60" fontFamily="longform">You don't have active advisory board seats yet. Accept an invite or apply to an open seat.</Text><HStack gap={2} wrap="wrap"><Button asChild={true} size="sm" variant="cta"><NextLink href={paths.opportunityMatches}>Browse matched seats</NextLink></Button><Button asChild={true} size="sm" variant="outline"><NextLink href={paths.opportunityInvites}>Check invites</NextLink></Button></HStack></VStack></Card.Body></Card.Root> : <VStack align="stretch" gap={3}>{active.map(engagement => {
        const founder = engagement.founderUser;
        const dmHref = founder?.userId ? paths.withQuery(paths.inbox, {
          founderUserId: founder.userId
        }) : paths.inbox;
        return <Card.Root key={engagement.advisorEngagementId} asChild={true} variant="interactive"><NextLink href={dmHref}><Card.Body><HStack gap={3} justify="space-between" wrap="wrap"><VStack align="start" gap={0}><Text fontWeight="semibold">{personName(founder, "Founder")}</Text><Text color="colorPalette.fg/60" fontSize="sm">{[engagement.category?.title, engagement.company?.name].filter(Boolean).join(" \xB7 ")}</Text>{engagement.activatedAt && <Text color="colorPalette.fg/50" fontSize="xs">Active since {formatWhen(engagement.activatedAt)}</Text>}</VStack><Button pointerEvents="none" size="sm" variant="cta">Open in Inbox</Button></HStack></Card.Body></NextLink></Card.Root>;
      })}<AdvisorsLoadMore hasNextPage={hasNextPage} loading={loadingMore} onLoadMore={loadMore} /></VStack>;
    $[9] = active;
    $[10] = hasNextPage;
    $[11] = loadMore;
    $[12] = loadingMore;
    $[13] = paths;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== query.error || $[16] !== t5 || $[17] !== t6) {
    t7 = <AdvisorsQueryState error={query.error} loading={t5}>{t6}</AdvisorsQueryState>;
    $[15] = query.error;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  return t7;
};

/** `/advisors/opportunities/engagements` — boards this advisor already sits on. */
export const OpportunitiesEngagementsSection = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <OpportunitiesAccessGate><OpportunitiesLayout activeKey="engagements" description="Boards you already advise. Jump into any conversation from here." title="Active engagements"><EngagementsBody /></OpportunitiesLayout></OpportunitiesAccessGate>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
function _temp(item) {
  return !!item?.advisorEngagementId;
}